import { TContractAcceptancePayload, TServiceAgreementResponse } from 'components/BizlyOS/Proposals/Contracting/types';
import { TSubmitVenueRebidPayload, TVenueRebid } from 'components/BizlyOS/Proposals/Rebid/types';
import { TCreditCardMethod, TProposalForm } from 'components/ProposalForm/types';
import { get, patch, post, put } from '.';

export const authProposalForm = (venueId: number | string, proposalId: number | string, token: string, email: string) =>
    get(`venues/${venueId}/proposals/${proposalId}/make-cookie?token=${token}&email=${encodeURIComponent(email)}`, {
        showErrorCode: true,
    });

export const getProposalForm = (venueId: number | string, proposalId: number | string) =>
    get(`venues/${venueId}/proposals/${proposalId}`, { showErrorCode: true }) as Promise<TProposalForm>;

export const putProposalForm = (venueId: number | string, proposalId: number | string, data: unknown) =>
    put(`venues/${venueId}/proposals/${proposalId}`, data) as Promise<TProposalForm>;

export const sendProposalForm = (venueId: number | string, proposalId: number | string) =>
    post(`venues/${venueId}/proposals/${proposalId}/send`) as Promise<TProposalForm>;

export const getClaimRedirectUrl = (venueId: number | string, proposalId: number | string) =>
    get(`venues/${venueId}/proposals/${proposalId}/claim-redirect`) as Promise<{ url: string }>;

export const dismissClaimPrompt = (venueId: number | string) =>
    post(`venues/${venueId}/claim-prompt/dismiss`) as Promise<{ success: boolean }>;

export const OTHER_REASON_ID = 1;

export const rejectProposalForm = (
    venueId: number | string,
    proposalId: number | string,
    data?: {
        rejectionReason: string;
        rejectionNotes: string;
    }
) => post(`venues/${venueId}/proposals/${proposalId}/reject`, data) as Promise<TProposalForm>;

export const cancelProposalForm = (venueId: number | string, proposalId: number | string, data?: unknown) =>
    post(`venues/${venueId}/proposals/${proposalId}/cancel`, data) as Promise<TProposalForm>;

export const postProposalFormContact = (venueId: number | string, proposalId: number | string, data: unknown) =>
    post(`venues/${venueId}/proposals/${proposalId}/contact`, data) as Promise<TProposalForm>;

export const exportProposals = (eventId: number | string) =>
    get(`events/${eventId}/proposals/export`).then(res => res.url);

export const getInquiryRejectionReasons = () =>
    get(`inquiry-rejection-reasons`) as Promise<{ inquiryRejectionReasons: BizlyAPI.InquiryRejectionReason[] }>;

export const rejectProposalExtension = (proposalId: number | string) =>
    post(`proposals/${proposalId}/reject-extension`);

export type TSpacePOST = Partial<{
    id: number;
    name: string;
    imageUrl: string;
    description: string | null;
    size: Area.Sqft | string | null;
    maxCapacity: number | string | null;
}>;

export const putVenueSpace = ({
    venueId,
    proposalId,
    spaceId,
    data,
}: {
    venueId: number | string;
    proposalId: number | string;
    spaceId: number | string;
    data: TSpacePOST;
}) => put(`venues/${venueId}/proposals/${proposalId}/spaces/${spaceId}`, data) as Promise<TProposalForm>;

export const postVenueSpace = ({
    venueId,
    proposalId,
    data,
}: {
    venueId: number | string;
    proposalId: number | string;
    data: TSpacePOST;
}) => post(`venues/${venueId}/proposals/${proposalId}/spaces`, data) as Promise<TProposalForm>;

export const getServiceAgreement = (venueId: number | string, proposalId: number | string) =>
    get(`venues/${venueId}/proposals/${proposalId}/service-agreement`, {
        showErrorCode: true,
    }) as Promise<TServiceAgreementResponse>;

export const patchServiceAgreement = (
    venueId: number | string,
    proposalId: number | string,
    agreementFields: Record<string, unknown>
) =>
    patch(
        `venues/${venueId}/proposals/${proposalId}/service-agreement`,
        { agreementFields },
        // Carry the HTTP status through so callers can tell a validation 422 (the user's
        // input, show it to them) from a 5xx (ours, report it).
        { showErrorCode: true }
    ) as Promise<TServiceAgreementResponse>;

export const postContractAcceptance = (
    venueId: number | string,
    proposalId: number | string,
    payload: TContractAcceptancePayload
) =>
    post(`venues/${venueId}/proposals/${proposalId}/contract-acceptance`, payload, {
        showErrorCode: true,
    }) as Promise<TServiceAgreementResponse>;

export type TPaymentSubmissionPayload = {
    invoiceDocumentUrl: string | null;
    invoiceAmount: number | null;
    invoiceCurrency: string | null;
    paymentMethod: TCreditCardMethod;
    paymentLinkUrl?: string | null;
    paymentFormDocumentUrl?: string | null;
    creditCardNotes?: string | null;
};

export const postPaymentSubmission = (
    venueId: number | string,
    proposalId: number | string,
    payload: TPaymentSubmissionPayload
) => post(`venues/${venueId}/proposals/${proposalId}/payment-submission`, payload) as Promise<TProposalForm>;

// A proposal that was never outbid 404s here — that's an expected, non-error outcome (the
// screen falls back to the normal proposal form), hence `showErrorCode` so callers can tell it
// apart from a real failure.
export const getVenueRebid = (venueId: number | string, proposalId: number | string) =>
    get(`venues/${venueId}/proposals/${proposalId}/rebid`, { showErrorCode: true }) as Promise<TVenueRebid>;

export const submitVenueRebid = (
    venueId: number | string,
    proposalId: number | string,
    payload: TSubmitVenueRebidPayload
) =>
    post(`venues/${venueId}/proposals/${proposalId}/rebid`, payload, {
        showErrorCode: true,
    }) as Promise<TVenueRebid>;
